/**
 * Reading a Foundation Data v2 citation off an answer, and matching it against the year's live rows.
 *
 * An answer records which approved figure it used as an entry in `issb_question.source_slices`, carrying
 * `foundationDatapointUid`, `foundationVersion`, `foundationYear`, `curatedPointId` and `dataKey`. The
 * figure's VALUE is not in that entry — Java strips `content` on the way in — so showing the number means
 * fetching the year's curated rows and finding the row again. That match is what this module owns.
 *
 * Pure functions in their own file: `src/utils/index.js` contains JSX so vitest cannot import the barrel
 * (see frontend/CLAUDE.md), and the matching rules below are the part worth pinning with tests.
 */

/**
 * A number that has crossed two json libraries and a string normaliser, or null.
 *
 * Three spellings reach us and only one is a plain integer. Production writes the YEAR **quoted** — the AI
 * service normalises the selected reporting years to strings before the draft (`ensure_strings` in
 * `backend-python/esgai/app.py`) and the entry carries that value through. gson's `IssbQuestion.clone()`
 * renders every number as a double, so a cloned question holds `2025.0`. A hand-written or backfilled row
 * can hold a bare `2025`. All three are the same year.
 *
 * Null for anything that is not a number at all, so a caller can branch on "not recorded" rather than
 * rendering `NaN`.
 *
 * @param {*} value
 * @returns {number|null}
 */
export function foundationNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.trunc(parsed);
}

/**
 * One approved figure as text: the value and its unit, exactly as approved.
 *
 * **Deliberately not formatted.** This is the one place in the pipeline where a figure is known to be
 * right — a person reviewed and signed it off — so reformatting the number here would put a figure nobody
 * approved in front of a reader. Same reasoning as `_content_of` in `backend-python/esgai/data/
 * foundation_data.py` and the table's own cell in `EditableMetricRow.jsx`. A range, `n/a` or prose passes
 * through untouched, which is exactly why `value_numeric` is allowed to be null upstream.
 *
 * @param {{dataValue?: string, unit?: string}|null|undefined} row
 * @returns {string} "" when there is no value to show
 */
export function foundationFigureText(row) {
  const value = row?.dataValue;
  if (value === null || value === undefined || value === "") return "";
  const unit = row?.unit;
  return unit ? `${value} ${unit}` : `${value}`;
}

/**
 * The curated row an answer's citation points at, and whether the figure has moved since.
 *
 * Two ways to match, in this order:
 *
 * 1. **`curatedPointId` against `row.id`.** A curated row's id names one VERSION, so a hit means the panel
 *    is showing the exact figure the answer was written from. `versionChanged` stays false even if the
 *    version numbers disagree: the id identifies the row and the row is authoritative over a number that was
 *    copied into a json blob at draft time.
 * 2. **`datapointUid`.** Saves are copy-on-write, so an edited figure is a NEW row with a new id and the
 *    old one archived. The uid is what survives that, and a hit whose `version` differs from the cited one
 *    is precisely the signal that the answer rests on a figure that has since been revised.
 *
 * A miss means the datapoint has no live row for that year — removed, or replaced by something that no
 * longer carries this uid. Note what cannot be done: `GET /years/{year}/curated` EXCLUDES archived rows
 * (`findByDataYear(..., LifecycleState.ARCHIVED)` is an exclusion), so the superseded version's own value is
 * not retrievable through any endpoint. A caller must say so rather than present the current figure as the
 * cited one.
 *
 * `undefined` rows (still loading) and `[]` rows (year holds nothing) both report `"none"`. Telling those
 * apart is request state, which belongs to the component, not here.
 *
 * @param {Array<object>|undefined|null} rows a year's curated rows
 * @param {{datapointUid?: string, curatedPointId?: number|string, version?: number|null}} citation
 * @returns {{row: object|null, match: "exact"|"datapoint"|"none", usedVersion: number|null,
 *            currentVersion: number|null, versionChanged: boolean}}
 */
export function resolveFoundationCitation(rows, citation) {
  const usedVersion = foundationNumber(citation?.version);
  const none = { row: null, match: "none", usedVersion, currentVersion: null, versionChanged: false };
  if (!rows?.length) return none;

  const citedPointId = foundationNumber(citation?.curatedPointId);
  if (citedPointId !== null) {
    const exact = rows.find((row) => foundationNumber(row?.id) === citedPointId);
    if (exact) {
      return {
        row: exact,
        match: "exact",
        usedVersion,
        currentVersion: foundationNumber(exact.version),
        versionChanged: false,
      };
    }
  }

  const uid = citation?.datapointUid;
  const byUid = uid ? rows.find((row) => String(row?.datapointUid) === String(uid)) : undefined;
  if (!byUid) return none;

  const currentVersion = foundationNumber(byUid.version);
  return {
    row: byUid,
    match: "datapoint",
    usedVersion,
    currentVersion,
    versionChanged: usedVersion !== null && currentVersion !== null && usedVersion !== currentVersion,
  };
}
